import React, { useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Player, PlayerRef } from '@remotion/player';
import { SimpleSlideshow } from '../compositions/SimpleSlideshow';
import { Panel } from '../types';
import { calculateTotalFrames } from '../utils/videoUtils';

interface SimpleVideoPlayerProps {
  panels: Panel[];
  panelDurations: number[];
  audioPath?: string;
  backgroundMusicUrl?: string;
  backgroundVolume?: number;
  aspectRatio?: '16:9' | '9:16';
  mutedTracks?: {
    voice: boolean;
    bgm: boolean;
    sfx: boolean;
  };
  playbackSpeed?: number;
  audioDuration?: number | null;
}

export interface SimpleVideoPlayerHandle {
  play: () => void;
  pause: () => void;
  seekTo: (frame: number) => void;
  getCurrentFrame: () => number;
  toggle: () => void;
}

export const SimpleVideoPlayer = forwardRef<SimpleVideoPlayerHandle, SimpleVideoPlayerProps>(
  (
    {
      panels,
      panelDurations,
      audioPath,
      backgroundMusicUrl,
      backgroundVolume = 0.25,
      aspectRatio = '9:16',
      mutedTracks = { voice: false, bgm: false, sfx: false },
      playbackSpeed = 1,
      audioDuration = null,
    },
    ref
  ) => {
    const playerRef = useRef<PlayerRef>(null);
    const renderCountRef = useRef(0);
    const lastFrameRef = useRef(0);
    const wasPlayingRef = useRef(false);
    
    renderCountRef.current++;
    // console.log('[SimpleVideoPlayer] Render #', renderCountRef.current, {
    //   panelsCount: panels.length,
    //   audioPath: audioPath?.substring(0, 50),
    //   bgmUrl: backgroundMusicUrl?.substring(0, 50),
    //   mutedTracks,
    //   playbackSpeed
    // });

    const fps = 60;
    let totalFrames = calculateTotalFrames(panels, panelDurations, fps);
    
    // If audio duration is available and longer than panels, use audio duration
    if (audioDuration) {
      const panelsTotalSec = totalFrames / fps;
      if (audioDuration > panelsTotalSec) {
        totalFrames = Math.ceil(audioDuration * fps);
        console.log('[SimpleVideoPlayer] Extended totalFrames to match audio duration:', audioDuration, 's');
      }
    }
    
    // Debug: Log total frames and durations
    console.log('[SimpleVideoPlayer] Total frames:', totalFrames, 'Total seconds:', totalFrames / fps, 'Panel durations:', panelDurations, 'Audio duration:', audioDuration);
    
    // Preserve playback state across re-renders
    useEffect(() => {
      if (playerRef.current && lastFrameRef.current > 0) {
        // Restore frame position after a brief delay to ensure player is ready
        const timer = setTimeout(() => {
          playerRef.current?.seekTo(lastFrameRef.current);
          if (wasPlayingRef.current) {
            playerRef.current?.play();
          }
        }, 50);
        
        return () => clearTimeout(timer);
      }
    }, [panels, panelDurations]);

    const dimensions =
      aspectRatio === '9:16'
        ? { width: 1080, height: 1920 }
        : { width: 1920, height: 1080 };

    // Expose player controls to parent
    useImperativeHandle(ref, () => ({
      play: () => {
        wasPlayingRef.current = true;
        playerRef.current?.play();
      },
      pause: () => {
        wasPlayingRef.current = false;
        lastFrameRef.current = playerRef.current?.getCurrentFrame() || 0;
        playerRef.current?.pause();
      },
      seekTo: (frame: number) => {
        lastFrameRef.current = frame;
        playerRef.current?.seekTo(frame);
      },
      getCurrentFrame: () => {
        const frame = playerRef.current?.getCurrentFrame() || 0;
        lastFrameRef.current = frame;
        return frame;
      },
      toggle: () => {
        wasPlayingRef.current = !wasPlayingRef.current;
        playerRef.current?.toggle();
      },
    }));

    if (panels.length === 0) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#000',
            color: '#666',
          }}
        >
          No panels available
        </div>
      );
    }

    return (
      <Player
        ref={playerRef}
        component={SimpleSlideshow as any}
        inputProps={{
          panels,
          panelDurations,
          audioPath: mutedTracks.voice ? undefined : audioPath,
          backgroundMusicUrl: mutedTracks.bgm ? undefined : backgroundMusicUrl,
          backgroundVolume,
          aspectRatio,
          backgroundColor: '#000000',
          textColor: '#ffffff',
          showTitles: false,
          showCaptions: false, // 🆕 Epic 6: Ẩn caption, chỉ dùng subtitle overlay
        }}
        durationInFrames={totalFrames}
        compositionWidth={dimensions.width}
        compositionHeight={dimensions.height}
        fps={fps}
        controls={false}
        playbackRate={playbackSpeed}
        style={{
          width: '100%',
          height: '100%',
          aspectRatio: aspectRatio === '9:16' ? '9/16' : '16/9',
        }}
      />
    );
  }
);

SimpleVideoPlayer.displayName = 'SimpleVideoPlayer';

export default SimpleVideoPlayer;
